import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { useOrg } from "../contexts/OrgProvider";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronRight, CheckCircle2, Clock, Circle, Send, Loader2,
  BookOpen, Shield, Target, Server, ArrowLeft, Sparkles,
  ShieldCheck, Building2, Leaf, FlaskConical, AlertTriangle, Package,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { TEXT_TONE } from "../components/ui/status-badge";
import { getStatusTone } from "../lib/statusTokens";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";
import { FW_COLORS as fwColors } from "../constants/frameworks";

const fwIcons = {
  iso27001: Shield, nist_scf: Target, cis_v8: Server, iso20000: BookOpen,
  oea: ShieldCheck, cnzfe: Building2, iso22000: Leaf, bpm: FlaskConical,
  haccp: AlertTriangle, nordom477: Package,
};

const ImplementationWizard = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const { activeFrameworks } = useOrg();
  const [frameworks, setFrameworks] = useState([]);
  const [selectedFw, setSelectedFw] = useState(null);
  const [phases, setPhases] = useState([]);
  const [activePhase, setActivePhase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  const fetchFrameworks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/wizard/frameworks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFrameworks(res.data);
    } catch (error) {
      console.error("Failed to fetch frameworks:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchPhases = useCallback(async (fw) => {
    try {
      const res = await axios.get(`${API}/wizard/${fw}/phases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhases(res.data);
    } catch (error) {
      console.error("Failed to fetch phases:", error);
    }
  }, [token]);

  useEffect(() => { fetchFrameworks(); }, [fetchFrameworks]);

  useEffect(() => {
    if (selectedFw) {
      fetchPhases(selectedFw);
      setChatMessages([]);
      setActivePhase(null);
    }
  }, [selectedFw, fetchPhases]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const selectPhase = (phase) => {
    setActivePhase(phase);
    setChatMessages([{
      role: "assistant",
      content: `Welcome to **Phase ${phase.phase}: ${phase.title}**\n\n${phase.description}\n\n**Key Deliverables:**\n${phase.key_deliverables.map(d => `- ${d}`).join('\n')}\n\nHow can I help you with this phase? You can ask me to:\n- Explain specific requirements\n- Generate policy/procedure templates\n- Suggest evidence to collect\n- Review your implementation approach`
    }]);
  };

  const updatePhaseStatus = async (phaseNum, status) => {
    try {
      await axios.put(
        `${API}/wizard/${selectedFw}/phase/${phaseNum}/status?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${t("wizard.phase")} ${phaseNum} ${t("wizard.marked_as")} ${status}`);
      fetchPhases(selectedFw);
    } catch (error) {
      toast.error(t("wizard.status_update_failed"));
    }
  };

  const sendMessage = async () => {
    if (!chatInput.trim() || !activePhase) return;
    const msg = chatInput.trim();
    setChatInput("");
    setChatMessages(prev => [...prev, { role: "user", content: msg }]);
    setChatLoading(true);

    try {
      const res = await axios.post(
        `${API}/wizard/${selectedFw}/ai-expert?phase=${activePhase.phase}`,
        { message: msg },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChatMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (error) {
      setChatMessages(prev => [...prev, { role: "assistant", content: "Sorry, I encountered an error. Please try again." }]);
    } finally {
      setChatLoading(false);
    }
  };

  const completedCount = phases.filter(p => p.status === "completed").length;
  const progressPct = phases.length > 0 ? Math.round((completedCount / phases.length) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">{t("wizard.loading")}</p>
        </div>
      </div>
    );
  }

  // Framework Selection
  if (!selectedFw) {
    return (
      <div className="p-6 space-y-6" data-testid="wizard-page">
        <PageHeader
          title={t("wizard.title")}
          description={t("wizard.subtitle")}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
          {frameworks.filter(fw => activeFrameworks.includes(fw.id)).map(fw => {
            const Icon = fwIcons[fw.id] || Shield;
            return (
              <motion.div key={fw.id} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Card
                  className="border-border hover:border-primary/50 transition-all cursor-pointer h-full"
                  onClick={() => setSelectedFw(fw.id)}
                  data-testid={`wizard-fw-${fw.id}`}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${fwColors[fw.id]}20` }}>
                        <Icon className="w-6 h-6" style={{ color: fwColors[fw.id] }} />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-lg">{fw.name}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{fw.description}</p>
                        <div className="flex items-center gap-3 mt-3">
                          <Badge variant="secondary">{fw.phases} {t("wizard.phases_label")}</Badge>
                          <Badge variant="secondary">{fw.total_controls} {t("wizard.controls_label")}</Badge>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-sm text-primary">
                      {t("wizard.start")} <ChevronRight className="w-4 h-4" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    );
  }

  // Wizard with Phases + AI Chat
  return (
    <div className="p-6 space-y-4" data-testid="wizard-active-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => { setSelectedFw(null); setActivePhase(null); }} data-testid="wizard-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-heading font-bold">{frameworks.find(f => f.id === selectedFw)?.name} {t("wizard.implementation")}</h1>
          <div className="flex items-center gap-3 mt-1">
            <Progress value={progressPct} className="h-2 flex-1 max-w-xs" />
            <span className="text-sm text-muted-foreground">{completedCount}/{phases.length} phases ({progressPct}%)</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 h-[calc(100vh-12rem)]">
        {/* Phase List - Left */}
        <div className="lg:col-span-4 xl:col-span-3">
          <Card className="border-border h-full">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{t("wizard.phases")}</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <ScrollArea className="h-[calc(100vh-18rem)]">
                <div className="space-y-1">
                  {phases.map(phase => {
                    const isActive = activePhase?.phase === phase.phase;
                    const StatusIcon = phase.status === "completed" ? CheckCircle2 : phase.status === "in_progress" ? Clock : Circle;
                    // phase.status usa "completed" (no "implemented" del kind canónico "impl");
                    // se traduce al vocabulario de STATUS_TONE antes de resolver el tone.
                    // P1 (ADR-P1-001): in_progress migra de amarillo a azul (cambio intencional).
                    const statusColor = TEXT_TONE[
                      getStatusTone("impl", phase.status === "completed" ? "implemented" : phase.status)
                    ];

                    return (
                      <div key={phase.phase} className={`p-3 rounded-lg cursor-pointer transition-all ${isActive ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted/50'}`}
                        onClick={() => selectPhase(phase)}
                        data-testid={`wizard-phase-${phase.phase}`}
                      >
                        <div className="flex items-start gap-3">
                          <StatusIcon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${statusColor}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{t("wizard.phase")} {phase.phase}</p>
                            <p className="text-xs text-muted-foreground truncate">{phase.title}</p>
                          </div>
                        </div>
                        {isActive && (
                          <div className="flex gap-1 mt-2 ml-8">
                            {phase.status !== "in_progress" && (
                              <Button size="sm" variant="outline" className="h-6 text-xs px-2"
                                onClick={(e) => { e.stopPropagation(); updatePhaseStatus(phase.phase, "in_progress"); }}>
                                {t("wizard.start_phase")}
                              </Button>
                            )}
                            {phase.status !== "completed" && (
                              <Button size="sm" variant="outline" className="h-6 text-xs px-2 text-green-600"
                                onClick={(e) => { e.stopPropagation(); updatePhaseStatus(phase.phase, "completed"); }}>
                                {t("wizard.complete_phase")}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* AI Expert Chat - Right */}
        <div className="lg:col-span-8 xl:col-span-9">
          <Card className="border-border h-full flex flex-col">
            <CardHeader className="pb-2 flex-shrink-0">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                <CardTitle className="text-sm">
                  {activePhase ? `${t("wizard.ai_expert")} - ${t("wizard.phase")} ${activePhase.phase}: ${activePhase.title}` : t("wizard.ai_expert")}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col p-0 min-h-0">
              {/* Messages */}
              <ScrollArea className="flex-1 px-4">
                <div className="space-y-4 py-4">
                  {!activePhase && chatMessages.length === 0 && (
                    <div className="text-center py-12">
                      <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                      <p className="text-muted-foreground">{t("wizard.select_phase")}</p>
                      <p className="text-xs text-muted-foreground mt-2">{t("wizard.ai_guide_desc")}</p>
                    </div>
                  )}
                  {chatMessages.map((msg, i) => {
                    // Render AI/user content as plain text with safe formatting.
                    // We split by newlines to preserve linebreaks, and replace
                    // **bold** runs with <strong>; everything else is escaped
                    // by React (text node), preventing XSS via prompt injection.
                    const lines = (msg.content || "").split("\n");
                    return (
                      <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[85%] rounded-xl px-4 py-3 ${
                          msg.role === "user"
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted/50 border border-border"
                        }`}>
                          <div className="text-sm whitespace-pre-wrap leading-relaxed">
                            {lines.map((line, idx) => {
                              const isBullet = /^- /.test(line);
                              const cleaned = isBullet ? line.slice(2) : line;
                              const parts = cleaned.split(/(\*\*[^*]+\*\*)/g);
                              return (
                                <div key={idx}>
                                  {isBullet && <span>&bull; </span>}
                                  {parts.map((p, j) =>
                                    p.startsWith("**") && p.endsWith("**") ? (
                                      <strong key={j}>{p.slice(2, -2)}</strong>
                                    ) : (
                                      <span key={j}>{p}</span>
                                    ),
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {chatLoading && (
                    <div className="flex justify-start">
                      <div className="bg-muted/50 border border-border rounded-xl px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          {t("wizard.analyzing")}
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
              </ScrollArea>

              {/* Input */}
              <div className="p-4 border-t border-border flex-shrink-0">
                {activePhase ? (
                  <div className="flex gap-2">
                    <Input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                      placeholder={`${t("wizard.ask_about_phase")} ${activePhase.phase}: ${activePhase.title}...`}
                      disabled={chatLoading}
                      data-testid="wizard-chat-input"
                    />
                    <Button onClick={sendMessage} disabled={chatLoading || !chatInput.trim()} data-testid="wizard-chat-send">
                      {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center">{t("wizard.select_phase_left")}</p>
                )}
                {activePhase && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {[
                      `What are the key requirements for ${activePhase.title}?`,
                      "Generate a policy template for this phase",
                      "What evidence do I need to collect?",
                      "What are common audit findings here?"
                    ].map((q, i) => (
                      <Button key={i} variant="ghost" size="sm" className="h-6 text-xs px-2 text-muted-foreground"
                        onClick={() => { setChatInput(q); }}
                        data-testid={`wizard-suggestion-${i}`}
                      >
                        {q.length > 40 ? q.substring(0, 40) + "..." : q}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ImplementationWizard;
